import React, { useMemo, useState } from "react";
import {
  Autocomplete,
  TextField,
  AutocompleteRenderOptionState,
  SxProps,
  Theme,
  InputAdornment,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import MDTypography from "components/MDTypography";
import { AddCircleOutline, Close } from "@mui/icons-material";

type OptionType = {
  label: string;
  value: string;
  [key: string]: any;
};

interface MDSelectProps {
  label?: string;
  value: OptionType | null;
  options: OptionType[];
  placeholder?: string;
  newInputValue?: any;
  isAddingNew: boolean;
  onChange: (value: OptionType | null) => void;
  onInputChange?: (inputValue: any) => void;
  onAddNewSubmit?: (value: string) => boolean;
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: OptionType,
    state: AutocompleteRenderOptionState
  ) => React.ReactNode;
  getOptionLabel?: (option: any) => string;
  filterOptions?: (options: OptionType[], state: { inputValue: string }) => OptionType[];
  disableClearable?: boolean;
  freeSolo?: boolean;
  loading?: boolean;
  disabled?: boolean;
  renderInputProps?: Partial<React.ComponentProps<typeof TextField>>;
  sx?: SxProps<Theme>;
  addNewOption?: () => React.ReactNode;
  enableValueEqualityCheck?: boolean;
}

const MDSelect: React.FC<MDSelectProps> = ({
  label = "",
  value,
  isAddingNew,
  newInputValue,
  options,
  onChange,
  onInputChange,
  renderOption,
  getOptionLabel = (option) => option.label,
  filterOptions,
  disableClearable = false,
  freeSolo = false,
  loading = false,
  disabled = false,
  renderInputProps = {},
  sx = {},
  addNewOption,
  onAddNewSubmit,
  enableValueEqualityCheck = false,
}) => {
  const [open, setOpen] = useState(false);
  const [newValue, setNewValue] = useState(newInputValue ?? "");
  const [error, setError] = useState("");

  const sortedOptions = useMemo(() => {
    const base = [...options].sort((a, b) => {
      const labelA = typeof a.label === "string" ? a.label : "";
      const labelB = typeof b.label === "string" ? b.label : "";
      return labelA.localeCompare(labelB);
    });

    if (addNewOption) {
      return [{ label: "Add New", value: "__add_new__", isAddNew: true }, ...base];
    }

    return base;
  }, [options, addNewOption]);

  if (isAddingNew) {
    return (
      <TextField
        autoFocus
        fullWidth
        variant="standard"
        value={newValue}
        onChange={(e) => setNewValue(e.target.value)}
        error={!!error}
        helperText={error}
        onKeyDown={(e) => {
          if (e.key === "Enter" && newValue.trim()) {
            const result = onAddNewSubmit?.(newValue.trim());
            if (!result) {
              setError("Name already exists.");
            } else {
              setError("");
            }
          } else if (e.key === "Escape") {
            onAddNewSubmit?.("");
            setError("");
          }
        }}
        onBlur={async () => {
          if (newValue.trim()) {
            const result = onAddNewSubmit?.(newValue.trim());
            if (!result) {
              setError("Name already exists.");
            } else {
              setError("");
            }
          } else {
            onAddNewSubmit?.("");
            setError("");
          }
        }}
        placeholder="Add New"
        label={
          <MDTypography fontSize={"0.75rem"} color="grey">
            {label}
          </MDTypography>
        }
        InputLabelProps={{ shrink: true }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => {
                  onAddNewSubmit?.("");
                  setNewValue("");
                }}
                size="small"
              >
                <Close fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={sx}
        {...renderInputProps}
      />
    );
  }

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={value}
      options={sortedOptions}
      getOptionLabel={getOptionLabel}
      filterOptions={filterOptions}
      disableClearable={disableClearable}
      freeSolo={freeSolo}
      loading={loading}
      disabled={disabled}
      popupIcon={open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      componentsProps={{ popper: { style: { width: "fit-content" } } }}
      sx={sx}
      onChange={(_, newValue: any) => {
        if (newValue.isAddNew) {
          setNewValue("");
        }
        onChange(newValue);
      }}
      onInputChange={(_, newInputValue) => {
        onInputChange?.(newInputValue);
      }}
      renderOption={(props, option, state) => {
        if (option.isAddNew) {
          return (
            <li
              {...props}
              key="add-new-option"
              style={{
                fontWeight: "bold",
                color: "#1976d2",
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 16px",
                cursor: "pointer",
              }}
            >
              <AddCircleOutline fontSize="small" />
              Add New
            </li>
          );
        }

        if (renderOption) return renderOption(props, option, state);

        return <li {...props}>{getOptionLabel(option)}</li>;
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={
            <MDTypography fontSize={"0.75rem"} color="grey">
              {label}
            </MDTypography>
          }
          variant="standard"
          {...renderInputProps}
        />
      )}
      isOptionEqualToValue={
        enableValueEqualityCheck ? (option, val) => option?.value === val?.value : undefined
      }
    />
  );
};

export default MDSelect;
